import { Tab, TabPanel, Tabs } from "@blueprintjs/core";
import { segmentTrackExportCompareResults } from "in_queue/analytics";
import { useChromeHeightVar } from "in_queue/hooks/useChromeHeightVar";
import { ScenarioMetadata } from "in_queue/types/scenarioType";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TrackedProject } from "../../types/projectType";
import {
    ScenarioAllocatedUpgrade,
    ScenarioConstraint,
    ScenarioProject,
    ScenarioResults
} from "../../types/scenarioResultType";
import { Compare, ExportOptions, ScopeConfig } from "../compare/Compare";
import { buildFileName } from "../compare/compareCsvExport";
import { FilterSlotContext } from "../compare/FilterSlotContext";
import css from "./CompareDetails.module.scss";
import { constraintsCompareColumns } from "./constraintsCompareColumns";
import { makeProjectsCompareColumns } from "./projectsCompareColumns";
import { upgradesCompareColumns } from "./upgradesCompareColumns";

const SCOPE_IDS = ["current", "related", "all"] as const;
type ScopeId = (typeof SCOPE_IDS)[number];
const isScopeId = (s: string): s is ScopeId => SCOPE_IDS.some((id) => id === s);

type ScenarioCompare = { projectId: string };

const ASSIGNED_UPGRADES_TAB_ID = "assigned-upgrades";
const CONSTRAINTS_TAB_ID = "constraints";
const PROJECTS_TAB_ID = "projects";
const COMPARE_TABS_ID = "compare-tabs";

const computeEligibleByScope = (
    resultsA: ScenarioResults,
    resultsB: ScenarioResults,
    currentProjectId: string
): Record<ScopeId, ReadonlySet<string>> => {
    const idsA = new Set(resultsA.projects.map((p) => p.projectId));
    const idsB = new Set(resultsB.projects.map((p) => p.projectId));

    const relatedUnion = new Set<string>();
    resultsA.relatedProjectIds.forEach((id) => relatedUnion.add(id));
    resultsB.relatedProjectIds.forEach((id) => relatedUnion.add(id));

    const all = new Set<string>();
    const related = new Set<string>();
    const current = new Set<string>();

    idsA.forEach((id) => {
        if (!idsB.has(id)) return;
        all.add(id);
        if (relatedUnion.has(id)) related.add(id);
        if (id === currentProjectId) current.add(id);
    });

    return { current, related, all };
};

export const CompareDetails: React.FC<{
    project: TrackedProject;
    resultsA: ScenarioResults;
    resultsB: ScenarioResults;
    fuelTypeByProjectId: Readonly<Record<string, string>>;
    scenarioA: ScenarioMetadata;
    scenarioB: ScenarioMetadata;
    isLoadingRemaining: boolean;
}> = ({
    project,
    resultsA,
    resultsB,
    fuelTypeByProjectId,
    scenarioA,
    scenarioB,
    isLoadingRemaining
}) => {
    const projectsCompareColumns = useMemo(
        () => makeProjectsCompareColumns(fuelTypeByProjectId),
        [fuelTypeByProjectId]
    );

    const eligibleByScope = useMemo(
        () => computeEligibleByScope(resultsA, resultsB, project.projectId),
        [resultsA, resultsB, project.projectId]
    );

    const applyScope = useCallback(
        (row: ScenarioCompare, s: string): boolean =>
            isScopeId(s) && eligibleByScope[s].has(row.projectId),
        [eligibleByScope]
    );

    const buildScopeProps = <TRow extends ScenarioCompare>(
        allowCurrentScope: boolean
    ): ScopeConfig<TRow> => ({
        options: allowCurrentScope
            ? [
                  { value: "current", label: `Only ${project.projectId}` },
                  { value: "related", label: "Related projects" },
                  { value: "all", label: "All projects" }
              ]
            : [
                  { value: "related", label: "Related projects" },
                  { value: "all", label: "All projects" }
              ],
        apply: applyScope
    });

    const buildExportOptions = (tabId: string): ExportOptions => ({
        labelA: scenarioA.title,
        labelB: scenarioB.title,
        exportFilename: buildFileName(
            { aLabel: scenarioA.title, bLabel: scenarioB.title },
            "csv",
            `${project.projectId}-${tabId}`
        ),
        onExportCallback: (success) => {
            if (success) {
                segmentTrackExportCompareResults({
                    projectId: project.projectId,
                    firstScenarioId: scenarioA.scenarioId,
                    secondScenarioId: scenarioB.scenarioId,
                    exportName: `${tabId}-results`
                });
            }
        }
    });

    const TABS = [
        {
            id: ASSIGNED_UPGRADES_TAB_ID,
            title: "Assigned upgrades",
            render: () => (
                <Compare<ScenarioAllocatedUpgrade>
                    rowsA={resultsA.allocatedUpgrades}
                    rowsB={resultsB.allocatedUpgrades}
                    columns={upgradesCompareColumns}
                    itemLabel="upgrades"
                    scopeOptions={buildScopeProps<ScenarioAllocatedUpgrade>(
                        true
                    )}
                    totalPending={isLoadingRemaining}
                    stickyHeader
                    exportOptions={buildExportOptions(ASSIGNED_UPGRADES_TAB_ID)}
                />
            )
        },
        {
            id: CONSTRAINTS_TAB_ID,
            title: "Constraints",
            render: () => (
                <Compare<ScenarioConstraint>
                    rowsA={resultsA.constraints}
                    rowsB={resultsB.constraints}
                    columns={constraintsCompareColumns}
                    itemLabel="constraints"
                    scopeOptions={buildScopeProps<ScenarioConstraint>(true)}
                    totalPending={isLoadingRemaining}
                    stickyHeader
                    exportOptions={buildExportOptions(CONSTRAINTS_TAB_ID)}
                />
            )
        },
        {
            id: PROJECTS_TAB_ID,
            title: "Projects",
            render: () => (
                <Compare<ScenarioProject>
                    rowsA={resultsA.projects}
                    rowsB={resultsB.projects}
                    columns={projectsCompareColumns}
                    itemLabel="projects"
                    scopeOptions={buildScopeProps<ScenarioProject>(false)}
                    chart={{
                        keyLabel: "Project",
                        isLoading: isLoadingRemaining
                    }}
                    stickyHeader
                    exportOptions={buildExportOptions(PROJECTS_TAB_ID)}
                />
            )
        }
    ];

    const [selectedTabId, setSelectedTabId] = useState<string>(
        ASSIGNED_UPGRADES_TAB_ID
    );

    const hostRef = useRef<HTMLDivElement | null>(null);
    const chromeRef = useRef<HTMLDivElement | null>(null);

    const [filterSlot, setFilterSlot] = useState<HTMLDivElement | null>(null);
    const filterSlotRef = useCallback((node: HTMLDivElement | null) => {
        setFilterSlot(node);
    }, []);

    useChromeHeightVar(hostRef, chromeRef, true);

    useEffect(() => {
        const host = hostRef.current;
        const chrome = chromeRef.current;
        if (host && chrome) {
            const hostTop = host.getBoundingClientRect().top;
            const chromeTop = chrome.getBoundingClientRect().top;
            if (hostTop < chromeTop - 1) {
                host.scrollIntoView({ block: "start" });
            }
        }
        const raf = requestAnimationFrame(() =>
            window.dispatchEvent(new Event("resize"))
        );
        return () => cancelAnimationFrame(raf);
    }, [selectedTabId]);

    return (
        <div ref={hostRef}>
            <div ref={chromeRef} className={css["sticky-chrome"]}>
                <div className={css["chrome-top-gap"]} />
                <Tabs
                    id={COMPARE_TABS_ID}
                    size="large"
                    selectedTabId={selectedTabId}
                    onChange={(id) => setSelectedTabId(String(id))}
                >
                    {TABS.map(({ id, title }) => (
                        <Tab key={id} id={id} title={title} />
                    ))}
                </Tabs>
                <div ref={filterSlotRef} className={css["filter-slot"]} />
            </div>
            {TABS.map(({ id, render }) => (
                <TabPanel
                    key={id}
                    id={id}
                    parentId={COMPARE_TABS_ID}
                    selectedTabId={selectedTabId}
                    className={css["table-fill"]}
                    panel={
                        <FilterSlotContext.Provider
                            value={id === selectedTabId ? filterSlot : null}
                        >
                            {render()}
                        </FilterSlotContext.Provider>
                    }
                />
            ))}
        </div>
    );
};
